import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Container, Tab, Tabs } from 'react-bootstrap'
import { GetFormat, PostFormat, useGet, usePost } from 'sharedKernel/api'
import { Loading } from 'components/loading'
import { PageTitle } from 'domain/feature/hub/pageTitle/styles'
import { TabHeader } from 'domain/feature/hub/company/challenge/program/components/tabHeader/tabHeader'
import { trans } from 'sharedKernel/i18n'
import { Filter } from 'components/Filter'
import { ECOSYSTEM_TABS, USER_FEATURES } from 'sharedKernel/constants'
import 'tippy.js/dist/tippy.css'
import 'tippy.js/animations/scale.css'
import 'tippy.js/themes/translucent.css'
import './styles.css'
import { FilterButton } from 'components/FilterButton/'
import { ValueType } from 'rsuite/esm/Checkbox'
import { Pagination } from 'rsuite'
import {
  IStartup,
  IConnection,
  IPublicAdministrationEcosystemRow,
  ecosystemStartupResidentCompanyId,
  DateRange,
  FilterItem,
} from './contracts'
import {
  mapPublicAdministrationRowToCard,
  mapRowToPublicAdministrationDetailState,
  normalizePublicAdministrationListResponse,
  readEcosystemHiddenFromRow,
  type PublicAdministrationListCardModel
} from './publicAdministrationMappers'
import {
  buildPublicAdministrationFilterBy,
  computePublicAdministrationFilterCounts,
  emptyPublicAdministrationFilterCounts,
  emptyPublicAdministrationSelectedFilters
} from './publicAdministrationFilters'
import { PublicAdministrationFilterSection } from './PublicAdministrationFilterSection'
import { TeiaNewStartupCard } from 'TEIA/components/NewStartupCard'
import { TeiaPublicAdministrationCard } from 'TEIA/components/PublicAdministrationCard'
import { showModal } from 'components/popup'
import { useLocation } from 'react-router'
import { useSmartNavigateLegacy as useNavigate } from 'components/SmartLink'
import { ConfirmationModal } from './confirmationModal'
import { StartupAlreadyExistsModal } from './startupAlreadyExistsModal'
import { HubStartupCompleteConnectionsTable } from './connections/table/complete'
import { postProvider, deleteProvider, getProvider } from 'sharedKernel/api/hooks/mutation'
import { showToast } from 'components/toast'
import { ConnectModal } from './connectModal'
import { PublicAdministrationConfirmationModal } from './publicAdministrationConfirmationModal'
import { QuickConnectModal } from './quickConnectModal'
import backgroundNoConnection from 'assets/images/defaults/backgroundNoConnection.png'
import { TReducers } from 'store'
import { cookies } from 'sharedKernel/cookies/behavior'
import { getCredLocalStorage } from 'sharedKernel/auth/credLocalStorage'
import {
  EcosystemPageHeader,
  EcosystemPageHeaderActions,
  EcosystemPageHeaderLeft,
  EcosystemSubtitle,
  SidebarItem,
  SidebarWrapper
} from './style'
import { triggerFileDownloadOnClientSide } from 'sharedKernel/file/handler'
import { BaseButton } from 'iebt-zing-storybook'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import DateRegisterFilter from 'components/DateRegisterFilter'
import { api } from 'sharedKernel/api/config'

interface EcosystemListPayload {
  rows: Array<{ ecosystemHidden?: boolean | null }>
  totalRows: number
  totalVisibleRows?: number | null
  ecosystemHiddenTotal?: number | null
}

const ecosystemHiddenCountInRows = (
  rows: Array<{ ecosystemHidden?: boolean | null }>
): number => rows.reduce((n, r) => n + (r.ecosystemHidden === true ? 1 : 0), 0)

function readOptionalNonNegativeNumber(payload: Record<string, unknown>, keys: string[]): number | undefined {
  for (const key of keys) {
    const v = payload[key]
    if (typeof v === 'number' && !Number.isNaN(v) && v >= 0) return v
  }
  return undefined
}

function tabTotalRowsForEcosystemList(payload: EcosystemListPayload): number {
  const flat = payload as unknown as Record<string, unknown>
  const rows = payload.rows ?? []

  const directVisible = readOptionalNonNegativeNumber(flat, [
    'totalVisibleRows',
    'total_visible_rows',
    'TotalVisibleRows'
  ])
  if (directVisible !== undefined) return directVisible

  const apiTotalRaw = flat.totalRows ?? flat.TotalRows
  const apiTotal =
    typeof apiTotalRaw === 'number' && !Number.isNaN(apiTotalRaw) ? apiTotalRaw : undefined

  const hiddenAgg = readOptionalNonNegativeNumber(flat, [
    'ecosystemHiddenTotal',
    'ecosystem_hidden_total',
    'EcosystemHiddenTotal'
  ])
  if (apiTotal !== undefined && hiddenAgg !== undefined) {
    return Math.max(0, apiTotal - hiddenAgg)
  }

  if (apiTotal !== undefined) return apiTotal

  return Math.max(0, rows.length - ecosystemHiddenCountInRows(rows))
}

function buildPublicAdministrationListPayload(
  data: unknown,
  rows: IPublicAdministrationEcosystemRow[],
  totalRows: number
): EcosystemListPayload {
  return {
    ...(typeof data === 'object' && data != null ? (data as EcosystemListPayload) : {}),
    rows: rows.map(row => ({ ecosystemHidden: readEcosystemHiddenFromRow(row) })),
    totalRows
  }
}

interface RequestPayload {
  filterBy: FilterItem[]
  sort: string
  limit: number
  page: number
  totalRows: number
  match: string
}

const SWIPER_TABS = {
  ZING: 0,
  MY: 1,
  CONNECTIONS: 2,
  HIDDEN: 3
}

const PUBLIC_ADMINISTRATION_TABS = {
  ALL: 0,
  RESIDENT: 1,
  HIDDEN: 2
}

type EcosystemMenuKey = 'startups' | 'publicAdministration'

const ECOSYSTEM_HIDE_ALLOWED_EMAIL = 'espacoteia@iebtinnovation.com'

export const HubCompanyEcosystemManagement = (): JSX.Element => {
  const location = useLocation() as { state?: { tab?: EcosystemMenuKey; subTab?: number; registerDateRange?: DateRange } }
  const navigate = useNavigate()
  const canManageEcosystemResidents = useSelector((state: TReducers) => {
    const hasChallenge = (state.auth.permissions?.features?.map((f) => f.code) ?? []).includes(
      USER_FEATURES.CHALLENGE
    )
    const companyType = getCredLocalStorage()?.company?.type?.type
    const isPrefecture =
      companyType === 'City Hall' || companyType === 'Admin City Hall'
    return hasChallenge && !isPrefecture
  })
  const currentUserEmail = (cookies.get('_cred')?.data?.email as string | undefined)?.trim().toLowerCase() ?? ''
  const canHideEcosystemItems = currentUserEmail === ECOSYSTEM_HIDE_ALLOWED_EMAIL
  const filterVisibleStartups = useCallback((rows: IStartup[]): IStartup[] => {
    return rows.filter(row => row.ecosystemHidden !== true)
  }, [canHideEcosystemItems])
  const isStartupHidden = (startup: IStartup): boolean => startup.ecosystemHidden === true
  const [ecosystemMenu, setEcosystemMenu] = useState<EcosystemMenuKey>(() => {
    const tab = location.state?.tab
    return tab === 'publicAdministration' ? 'publicAdministration' : 'startups'
  })
  const [activeSection, setActiveSection] = useState(() => {
    if (location.state?.tab !== 'startups') return 0
    const sub = location.state?.subTab
    return sub === 1 || sub === 2 ? sub : 0
  })
  const [tabValue, setTabValue] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalRows, setTotalRows] = useState(0)
  const [residentStartupData, setResidentStartupData] = useState<IStartup[]>([])
  const [residentTotalRows, setResidentTotalRows] = useState(0)
  const [connectionTotalRows, setConnectionTotalRows] = useState(0)
  const [filteredStartups, setFilteredStartups] = useState<IStartup[]>([])
  const [filteredResidents, setFilteredResidents] = useState<IStartup[]>([])
  const [startupsFilter, setStartupsFilter] = useState<IStartup[]>([])
  const [residentsFilter, setResidentsFilter] = useState<IStartup[]>([])

  const { mutate: downloadExportFile } = getProvider()

  const [filterCounts, setFilterCounts] = useState({
    address_state: 0,
    activity: 0,
    business_model: 0,
    number_employees: 0,
    target: 0,
    investment_source: 0,
    development_stage: 0,
    has_raised_investment: 0
  })
  const [totalFilterCount, setTotalFilterCount] = useState(0)

  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({
    company_address_state: [],
    startup_info_activity: [],
    business_model: [],
    number_employees: [],
    startup_info_target: [],
    startup_info_investment_source: [],
    startup_info_development_stage: [],
    startup_info_has_raised_investment: []
  })
  const [lastQuickConnectionData, setLastQuickConnectionData] = useState<any | null>(null)
  const [publicAdministrationActiveTab, setPublicAdministrationActiveTab] = useState(() => {
    const isPublicAdministrationTab = location.state?.tab === 'publicAdministration'
    if (!isPublicAdministrationTab) return PUBLIC_ADMINISTRATION_TABS.ALL

    const subTab = location.state?.subTab
    if (subTab === PUBLIC_ADMINISTRATION_TABS.RESIDENT || subTab === PUBLIC_ADMINISTRATION_TABS.HIDDEN) return subTab

    return PUBLIC_ADMINISTRATION_TABS.ALL
  })

  const [publicAdministrationSelectedFilters, setPublicAdministrationSelectedFilters] = useState(() =>
    emptyPublicAdministrationSelectedFilters()
  )
  const [publicAdministrationFilterCounts, setPublicAdministrationFilterCounts] = useState(
    emptyPublicAdministrationFilterCounts
  )
  const [publicAdministrationTotalFilterCount, setPublicAdministrationTotalFilterCount] = useState(0)
  const [publicAdministrationSearchTerm, setPublicAdministrationSearchTerm] = useState('')
  const [publicAdministrationSearchFilter, setPublicAdministrationSearchFilter] = useState('')

  const [hiddenStartupPage, setHiddenStartupPage] = useState(1)
  const [hiddenStartupTotalRows, setHiddenStartupTotalRows] = useState(0)
  const [hiddenStartups, setHiddenStartups] = useState<IStartup[]>([])

  const filterBarRefStartups = useRef<HTMLDivElement>(null)
  const filterBarRefPublicAdministration = useRef<HTMLDivElement>(null)

  const [publicAdministrationPage, setPublicAdministrationPage] = useState(1)
  const publicAdministrationPageSize = 10
  const [publicAdministrationAllTabTotal, setPublicAdministrationAllTabTotal] = useState(0)
  const [publicAdministrationResidentTabTotal, setPublicAdministrationResidentTabTotal] = useState(0)
  const [publicAdministrationDisplayCardRows, setPublicAdministrationDisplayCardRows] = useState<
    PublicAdministrationListCardModel[]
  >([])
  const [publicAdministrationDisplayTotalRows, setPublicAdministrationDisplayTotalRows] = useState(0)

  const [hiddenPublicAdministrationTotalRows, setHiddenPublicAdministrationTotalRows] = useState(0)
  const [hiddenPublicAdministrationPage, setHiddenPublicAdministrationPage] = useState(1)

  const emptyDateRange: DateRange = { start: null, end: null }

  const resolveInitialDateRange = (stored: DateRange | undefined, fallback: DateRange): DateRange => {
    if (stored != null) {
      return {
        start: stored.start != null ? new Date(stored.start) : null,
        end: stored.end != null ? new Date(stored.end) : null
      }
    }
    return fallback
  }

  const [searchFilter, setSearchFilter] = useState<string>('')
  const [registerDateRange, setRegisterDateRange] = useState<DateRange>(() =>
    resolveInitialDateRange(location.state?.registerDateRange, emptyDateRange)
  )

  const registerDateRangeKey = `${registerDateRange.start?.getTime() ?? 0}_${registerDateRange.end?.getTime() ?? 0}`

  const labels = {
    pageTitle: canManageEcosystemResidents ? trans('Gerenciar Ecossistema') : trans('Ecossistema'),
    startups: trans('Startups'),
    investors: trans('Investidores'),
    company: trans('Empresa'),
    evaluators: trans('Avaliadores'),
    addResidentSuccess: trans('Startup adicionada com sucesso!'),
    removeResidentSuccess: trans('Startup removida com sucesso!')
  }

  const buildFilterBy = (): FilterItem[] => {
    const filterBy: FilterItem[] = []

    const fieldMapping: Record<string, string> = {
      company_address_state: 'company.address_state',
      startup_info_activity: 'startup_info.activity',
      business_model: 'startup_info.business_model',
      number_employees: 'startup_info.number_employees',
      startup_info_target: 'startup_info.target',
      startup_info_investment_source: 'startup_info.investment_source',
      startup_info_development_stage: 'startup_info.development_stage',
      startup_info_has_raised_investment: 'startup_info.has_raised_investment'
    }

    Object.entries(selectedFilters).forEach(([field, values]) => {
      if (values.length === 0) return
      const mappedField = fieldMapping[field] ?? field
      if (values.length === 1) {
        filterBy.push({
          field: mappedField,
          condition: values[0],
          comparison: 'eq'
        })
      } else {
        filterBy.push({
          field: mappedField,
          condition: values.join('|'),
          comparison: 'in'
        })
      }
    })

    if (searchFilter.trim() !== '') {
      filterBy.push({
        field: 'company.name',
        condition: searchFilter.trim(),
        comparison: 'lk'
      })
    }

    if (ecosystemMenu === 'startups') {
      const dateField = 'company.created_at'
      if (registerDateRange.start && registerDateRange.end) {
        filterBy.push({
          field: dateField,
          condition: `${registerDateRange.start.toISOString().slice(0, 10)} ${registerDateRange.end.toISOString().slice(0, 10)}`,
          comparison: 'bt'
        })
      } else if (registerDateRange.start) {
        filterBy.push({
          field: dateField,
          condition: registerDateRange.start.toISOString().slice(0, 10),
          comparison: 'eq'
        })
      }
    } else if (ecosystemMenu === 'publicAdministration') {
      const dateField = 'public_administration.created_at'
      if (registerDateRange.start && registerDateRange.end) {
        filterBy.push({
          field: dateField,
          condition: `${registerDateRange.start.toISOString().slice(0, 10)} ${registerDateRange.end.toISOString().slice(0, 10)}`,
          comparison: 'bt'
        })
      } else if (registerDateRange.start) {
        filterBy.push({
          field: dateField,
          condition: registerDateRange.start.toISOString().slice(0, 10),
          comparison: 'eq'
        })
      }
    }

    return filterBy
  }

  const LARGE_PAGE_FETCH_SIZE = 500
  const startupsServerLimit = canHideEcosystemItems ? pageSize : LARGE_PAGE_FETCH_SIZE
  const startupsServerPage = canHideEcosystemItems ? currentPage : 1

  const requestPayload: RequestPayload = {
    filterBy: buildFilterBy(),
    sort: '',
    limit: startupsServerLimit,
    page: startupsServerPage,
    totalRows: 0,
    match: ''
  }

  const residentRequestPayload: RequestPayload = {
    filterBy: buildFilterBy(),
    sort: '',
    limit: startupsServerLimit,
    page: startupsServerPage,
    totalRows: 0,
    match: ''
  }

  const connectionRequestPayload: RequestPayload = {
    filterBy: buildFilterBy(),
    sort: '',
    limit: startupsServerLimit,
    page: startupsServerPage,
    totalRows: 0,
    match: ''
  }

  const hiddenStartupRequestPayload: RequestPayload = {
    filterBy: buildFilterBy(),
    sort: '',
    limit: LARGE_PAGE_FETCH_SIZE,
    page: 1,
    totalRows: 0,
    match: ''
  }

  const startupsFilterRequest: GetFormat = {
    url: '/v1/hub/company/ecosystem/filter'
  }

  const residentsFilterRequest: GetFormat = {
    url: '/v1/hub/company/ecosystem/resident/filter'
  }

  const request: PostFormat = {
    url: '/v1/hub/company/ecosystem/startups',
    data: requestPayload
  }

  const residentRequest: PostFormat = {
    url: '/v1/hub/company/ecosystem/startups/resident',
    data: residentRequestPayload
  }

  const connectionRequest: PostFormat = {
    url: '/v1/hub/company/ecosystem/startups/connection',
    data: connectionRequestPayload
  }

  const hiddenStartupRequest: PostFormat = {
    url: '/v1/hub/company/ecosystem/startups',
    data: hiddenStartupRequestPayload
  }

  const publicAdministrationListPayload: RequestPayload = {
    filterBy: buildPublicAdministrationFilterBy(
      publicAdministrationSelectedFilters,
      publicAdministrationSearchFilter.trim(),
      registerDateRange
    ),
    sort: '',
    limit: LARGE_PAGE_FETCH_SIZE,
    page: 1,
    totalRows: 0,
    match: ''
  }

  const publicAdministrationAllRequest: PostFormat = {
    url: '/v1/hub/company/ecosystem/public-administration',
    data: publicAdministrationListPayload
  }

  const publicAdministrationResidentRequest: PostFormat = {
    url: '/v1/hub/company/ecosystem/public-administration/resident',
    data: publicAdministrationListPayload
  }

  const hiddenPublicAdministrationRequestPayload: RequestPayload = {
    filterBy: [
      ...buildPublicAdministrationFilterBy(
        publicAdministrationSelectedFilters,
        publicAdministrationSearchFilter.trim(),
        registerDateRange
      ),
      { field: 'company.ecosystem_hidden', condition: 'true', comparison: 'eq' }
    ],
    sort: '',
    limit: publicAdministrationPageSize,
    page: hiddenPublicAdministrationPage,
    totalRows: 0,
    match: ''
  }

  const hiddenPublicAdministrationRequest: PostFormat = {
    url: '/v1/hub/company/ecosystem/public-administration',
    data: hiddenPublicAdministrationRequestPayload
  }

  const ecosystemPublicAdministrationEnabled = ecosystemMenu === 'publicAdministration'

  const {
    data: publicAdministrationAllData,
    isFetching: isPublicAdministrationAllFetching,
    refetch: publicAdministrationAllRefetch
  } = usePost<unknown>(
    publicAdministrationAllRequest,
    `ecosystem-public-administration-all_${registerDateRangeKey}`,
    { enabled: ecosystemPublicAdministrationEnabled }
  )

  const {
    data: publicAdministrationResidentData,
    isFetching: isPublicAdministrationResidentFetching,
    refetch: publicAdministrationResidentRefetch
  } = usePost<unknown>(
    publicAdministrationResidentRequest,
    `ecosystem-public-administration-resident_${registerDateRangeKey}`,
    { enabled: ecosystemPublicAdministrationEnabled }
  )

  const {
    data: publicAdministrationHiddenData,
    isFetching: isPublicAdministrationHiddenFetching,
    refetch: publicAdministrationHiddenRefetch
  } = usePost<unknown>(
    hiddenPublicAdministrationRequest,
    `ecosystem-public-administration-hidden_${hiddenPublicAdministrationPage}`,
    { enabled: ecosystemPublicAdministrationEnabled && canHideEcosystemItems }
  )

  const startupsEcosystemEnabled = ecosystemMenu === 'startups'

  const {
    data: startupData,
    isFetching: isStartupsFetching,
    refetch: startupsRefetch
  } = usePost<EcosystemListPayload & { rows: IStartup[] }>(request, `startup-data_${registerDateRangeKey}`,
    { enabled: startupsEcosystemEnabled })

  const {
    data: residentData,
    isFetching: isResidentFetching,
    refetch: residentRefetch
  } = usePost<EcosystemListPayload & { rows: IStartup[] }>(residentRequest, `resident-startup-data_${registerDateRangeKey}`,
    { enabled: startupsEcosystemEnabled })

  const {
    data: connectionData,
    isFetching: isConnectionFetching,
    refetch: connectionRefetch
  } = usePost<EcosystemListPayload & { rows: IStartup[] }>(connectionRequest, `connection-startup-data_${registerDateRangeKey}`,
    { enabled: startupsEcosystemEnabled })

  const {
    data: hiddenStartupData,
    isFetching: isHiddenStartupFetching,
    refetch: hiddenStartupsRefetch
  } = usePost<EcosystemListPayload & { rows: IStartup[] }>(
    hiddenStartupRequest,
    `hidden-startup-data_${registerDateRangeKey}`,
    { enabled: startupsEcosystemEnabled && canHideEcosystemItems }
  )

  const {
    data: startupFilterData,
    isFetching: isStartupFilterDataFetching
  } = useGet<IStartup[]>(startupsFilterRequest, 'startup-filter-data')

  const {
    data: residentFilterData,
    isFetching: isResidentFilterDataFetching
  } = useGet<IStartup[]>(residentsFilterRequest, 'resident-filter-data')

  const toggleStartupHiddenInEcosystemList = useCallback(async (startup: IStartup): Promise<void> => {
    const targetCompanyId = ecosystemStartupResidentCompanyId(startup)
    const isCurrentlyHidden = startup.ecosystemHidden === true
    const hide = !isCurrentlyHidden
    try {
      await api.patch(`/v1/hub/company/${targetCompanyId}/ecosystem/hide/${hide}`)
      showToast('success', hide ? 'Startup ocultada com sucesso.' : 'Startup ativada com sucesso.')
      await Promise.all([startupsRefetch(), residentRefetch(), connectionRefetch(), hiddenStartupsRefetch()])
    } catch {
      showToast('error', 'Não foi possível alterar a visibilidade da startup. Tente novamente.')
    }
  }, [startupsRefetch, residentRefetch, connectionRefetch, hiddenStartupsRefetch])

  const togglePublicAdministrationHiddenInEcosystemList = useCallback(
    async (publicAdministration: PublicAdministrationListCardModel): Promise<void> => {
      const hide = !publicAdministration.ecosystemHidden
      try {
        await api.patch(
          `/v1/hub/company/${publicAdministration.residentCompanyId}/ecosystem/hide/${hide}`
        )
        showToast(
          'success',
          hide ? 'Órgão público ocultado com sucesso.' : 'Órgão público ativado com sucesso.'
        )
        await Promise.all([publicAdministrationAllRefetch(), publicAdministrationResidentRefetch(), publicAdministrationHiddenRefetch()])
      } catch {
        showToast(
          'error',
          'Não foi possível alterar a visibilidade do órgão público. Tente novamente.'
        )
      }
    },
    [publicAdministrationAllRefetch, publicAdministrationResidentRefetch, publicAdministrationHiddenRefetch]
  )

  useEffect(() => {
    if (!isStartupFilterDataFetching && startupFilterData !== undefined && startupFilterData !== null) {
      setStartupsFilter(startupFilterData)
    }
  }, [isStartupFilterDataFetching, startupFilterData])

  useEffect(() => {
    if (!isResidentFilterDataFetching && residentFilterData !== undefined && residentFilterData !== null) {
      setResidentsFilter(residentFilterData)
    }
  }, [isResidentFilterDataFetching, residentFilterData])

  useEffect(() => {
    const counts = {
      address_state: selectedFilters.company_address_state.length,
      activity: selectedFilters.startup_info_activity.length,
      business_model: selectedFilters.business_model.length,
      number_employees: selectedFilters.number_employees.length,
      target: selectedFilters.startup_info_target.length,
      investment_source: selectedFilters.startup_info_investment_source.length,
      development_stage: selectedFilters.startup_info_development_stage.length,
      has_raised_investment: selectedFilters.startup_info_has_raised_investment.length
    }

    setFilterCounts(counts)
    setTotalFilterCount(Object.values(counts).reduce((sum, count) => sum + count, 0))
  }, [selectedFilters])

  useEffect(() => {
    const counts = computePublicAdministrationFilterCounts(publicAdministrationSelectedFilters)
    setPublicAdministrationFilterCounts(counts)
    setPublicAdministrationTotalFilterCount(Object.values(counts).reduce((sum, count) => sum + count, 0))
  }, [publicAdministrationSelectedFilters])

  useEffect(() => {
    switch (activeSection) {
      case SWIPER_TABS.ZING:
        changeTabActions('')
        break
      case SWIPER_TABS.MY:
        changeTabActions(ECOSYSTEM_TABS.INVESTORS)
        break
    }
  }, [activeSection, residentRefetch])

  const sliceForLocalPage = useCallback(<T,>(rows: T[]): T[] => {
    if (canHideEcosystemItems) return rows
    const startIndex = (currentPage - 1) * pageSize
    return rows.slice(startIndex, startIndex + pageSize)
  }, [canHideEcosystemItems, currentPage, pageSize])

  const sliceForLocalPublicAdministrationPage = useCallback(
    (rows: PublicAdministrationListCardModel[]): PublicAdministrationListCardModel[] => {
      const startIndex = (publicAdministrationPage - 1) * publicAdministrationPageSize
      return rows.slice(startIndex, startIndex + publicAdministrationPageSize)
    },
    [publicAdministrationPage, publicAdministrationPageSize]
  )

  const sliceForHiddenStartupPage = useCallback((rows: IStartup[]): IStartup[] => {
    const startIndex = (hiddenStartupPage - 1) * pageSize
    return rows.slice(startIndex, startIndex + pageSize)
  }, [hiddenStartupPage, pageSize])

  useEffect(() => {
    if (!isStartupsFetching && startupData !== undefined && startupData !== null) {
      const visibleRows = filterVisibleStartups(startupData.rows ?? [])
      const total = tabTotalRowsForEcosystemList(startupData)
      setFilteredStartups(sliceForLocalPage(visibleRows))
      setTotalRows(total)
    }
  }, [isStartupsFetching, startupData, filterVisibleStartups, sliceForLocalPage])

  useEffect(() => {
    if (!isResidentFetching && residentData !== undefined && residentData !== null) {
      const visibleRows = filterVisibleStartups(residentData.rows ?? [])
      const total = tabTotalRowsForEcosystemList(residentData)
      const pagedRows = sliceForLocalPage(visibleRows)
      setFilteredResidents(pagedRows)
      setResidentStartupData(pagedRows)
      setResidentTotalRows(total)
    }
  }, [isResidentFetching, residentData, filterVisibleStartups, sliceForLocalPage])

  useEffect(() => {
    if (!isConnectionFetching && connectionData !== undefined && connectionData !== null) {
      const visibleRows = filterVisibleStartups(connectionData.rows ?? [])
      const total = tabTotalRowsForEcosystemList(connectionData)
      setConnectionTotalRows(total)
    }
  }, [isConnectionFetching, connectionData, filterVisibleStartups])

  useEffect(() => {
    if (isHiddenStartupFetching || hiddenStartupData == null) return

    const hiddenRows = (hiddenStartupData.rows ?? []).filter(row => row.ecosystemHidden === true)
    const flat = hiddenStartupData as unknown as Record<string, unknown>
    const totalFromMeta = readOptionalNonNegativeNumber(flat, [
      'ecosystemHiddenTotal',
      'ecosystem_hidden_total',
      'EcosystemHiddenTotal'
    ])
    const total = totalFromMeta !== undefined ? totalFromMeta : hiddenRows.length

    setHiddenStartups(sliceForHiddenStartupPage(hiddenRows))
    setHiddenStartupTotalRows(total)
  }, [isHiddenStartupFetching, hiddenStartupData, sliceForHiddenStartupPage])

  const refetchAllPublicAdministrationLists = useCallback((): void => {
    publicAdministrationAllRefetch()
    publicAdministrationResidentRefetch()
    if (canHideEcosystemItems) publicAdministrationHiddenRefetch()
  }, [publicAdministrationAllRefetch, publicAdministrationResidentRefetch, publicAdministrationHiddenRefetch, canHideEcosystemItems])

  useEffect(() => {
    setPublicAdministrationPage(1)
    if (ecosystemMenu === 'publicAdministration') {
      refetchAllPublicAdministrationLists()
    }
  }, [publicAdministrationSelectedFilters, publicAdministrationSearchFilter, registerDateRangeKey, ecosystemMenu, refetchAllPublicAdministrationLists])

  useEffect(() => {
    if (ecosystemMenu !== 'publicAdministration') return
    if (publicAdministrationActiveTab === PUBLIC_ADMINISTRATION_TABS.HIDDEN && canHideEcosystemItems) {
      publicAdministrationHiddenRefetch()
    }
  }, [hiddenPublicAdministrationPage, ecosystemMenu, publicAdministrationActiveTab, publicAdministrationHiddenRefetch, canHideEcosystemItems])

  useEffect(() => {
    if (!isPublicAdministrationAllFetching && publicAdministrationAllData != null) {
      const list = normalizePublicAdministrationListResponse(publicAdministrationAllData)
      const total = tabTotalRowsForEcosystemList(
        buildPublicAdministrationListPayload(publicAdministrationAllData, list.rows, list.totalRows)
      )
      setPublicAdministrationAllTabTotal(total)
    }
  }, [isPublicAdministrationAllFetching, publicAdministrationAllData])

  useEffect(() => {
    if (!isPublicAdministrationResidentFetching && publicAdministrationResidentData != null) {
      const list = normalizePublicAdministrationListResponse(publicAdministrationResidentData)
      const total = tabTotalRowsForEcosystemList(
        buildPublicAdministrationListPayload(
          publicAdministrationResidentData,
          list.rows,
          list.totalRows
        )
      )
      setPublicAdministrationResidentTabTotal(total)
    }
  }, [isPublicAdministrationResidentFetching, publicAdministrationResidentData])

  useEffect(() => {
    if (!isPublicAdministrationHiddenFetching && publicAdministrationHiddenData != null) {
      const list = normalizePublicAdministrationListResponse(publicAdministrationHiddenData)
      const hiddenRows = list.rows.filter(row => readEcosystemHiddenFromRow(row))
      const flat = publicAdministrationHiddenData as Record<string, unknown>
      const totalFromMeta = readOptionalNonNegativeNumber(flat, [
        'ecosystemHiddenTotal',
        'ecosystem_hidden_total',
        'EcosystemHiddenTotal'
      ])
      setHiddenPublicAdministrationTotalRows(totalFromMeta !== undefined ? totalFromMeta : hiddenRows.length)
    }
  }, [isPublicAdministrationHiddenFetching, publicAdministrationHiddenData])

  useEffect(() => {
    if (ecosystemMenu !== 'publicAdministration') return

    const isAllTab = publicAdministrationActiveTab === PUBLIC_ADMINISTRATION_TABS.ALL
    const isResidentTab = publicAdministrationActiveTab === PUBLIC_ADMINISTRATION_TABS.RESIDENT
    const isHiddenTab = publicAdministrationActiveTab === PUBLIC_ADMINISTRATION_TABS.HIDDEN
    let sourceData
    let isSourceFetching
    if (isAllTab) {
      sourceData = publicAdministrationAllData
      isSourceFetching = isPublicAdministrationAllFetching
    } else if (isResidentTab) {
      sourceData = publicAdministrationResidentData
      isSourceFetching = isPublicAdministrationResidentFetching
    } else if (isHiddenTab) {
      sourceData = publicAdministrationHiddenData
      isSourceFetching = isPublicAdministrationHiddenFetching
    }
    if (isSourceFetching || sourceData == null) return

    const list = normalizePublicAdministrationListResponse(sourceData)
    let visibleRows: IPublicAdministrationEcosystemRow[] = list.rows
    if (isAllTab || isResidentTab) {
      visibleRows = list.rows.filter(row => !readEcosystemHiddenFromRow(row))
    } else if (isHiddenTab) {
      visibleRows = list.rows.filter(row => readEcosystemHiddenFromRow(row))
    }

    const cards = visibleRows.map(mapPublicAdministrationRowToCard)

    if (isHiddenTab) {
      const flat = sourceData as Record<string, unknown>
      const totalFromMeta = readOptionalNonNegativeNumber(flat, [
        'ecosystemHiddenTotal',
        'ecosystem_hidden_total',
        'EcosystemHiddenTotal'
      ])
      const total = totalFromMeta !== undefined ? totalFromMeta : cards.length
      setPublicAdministrationDisplayCardRows(sliceForLocalPublicAdministrationPage(cards))
      setPublicAdministrationDisplayTotalRows(total)
    } else {
      const total = tabTotalRowsForEcosystemList(
        buildPublicAdministrationListPayload(sourceData, list.rows, list.totalRows)
      )
      setPublicAdministrationDisplayCardRows(sliceForLocalPublicAdministrationPage(cards))
      setPublicAdministrationDisplayTotalRows(total)
    }
  }, [
    ecosystemMenu,
    publicAdministrationActiveTab,
    isPublicAdministrationAllFetching,
    isPublicAdministrationResidentFetching,
    isPublicAdministrationHiddenFetching,
    publicAdministrationAllData,
    publicAdministrationResidentData,
    publicAdministrationHiddenData,
    canHideEcosystemItems,
    publicAdministrationPage,
    publicAdministrationPageSize,
    sliceForLocalPublicAdministrationPage
  ])

  const getCurrentFilters = (): IStartup[] => {
    if (activeSection === SWIPER_TABS.ZING) {
      return startupsFilter
    }
    if (activeSection === SWIPER_TABS.MY) {
      return residentsFilter.length > 0 ? residentsFilter : residentStartupData
    }
    return residentsFilter
  }

  const getFilteredDataset = (): IStartup[] => {
    return activeSection === SWIPER_TABS.ZING ? filteredStartups : filteredResidents
  }

  const mapConnectionsToTableData = (): any[] => {
    if (connectionData?.rows === undefined || connectionData.rows.length === 0) {
      return []
    }

    const connections: any[] = []
    sliceForLocalPage(filterVisibleStartups(connectionData.rows)).forEach((startup: IStartup) => {
      if (startup.connections !== undefined && startup.connections.length > 0) {
        startup.connections.forEach((connection: IConnection) => {
          connections.push({
            startup: startup.companyName === '' || startup.companyName == null ? '-' : startup.companyName,
            responsible: connection.responsible,
            interestArea: connection.areaInterest,
            metWeb: connection.source,
            description: connection.description,
            startDate: connection.createdAt,
            lastUpdate: connection.updatedAt
          })
        })
      }
    })

    return connections
  }

  const refetchAllStartupEcosystemLists = useCallback((): void => {
    startupsRefetch()
    residentRefetch()
    connectionRefetch()
    if (canHideEcosystemItems) hiddenStartupsRefetch()
  }, [startupsRefetch, residentRefetch, connectionRefetch, hiddenStartupsRefetch, canHideEcosystemItems])

  useEffect(() => {
    setCurrentPage(1)
    if (ecosystemMenu === 'startups') {
      refetchAllStartupEcosystemLists()
    }
  }, [selectedFilters, searchFilter, registerDateRangeKey, ecosystemMenu, refetchAllStartupEcosystemLists])

  useEffect(() => {
    if (ecosystemMenu !== 'startups') return
    if (activeSection === SWIPER_TABS.ZING) {
      startupsRefetch()
    } else if (activeSection === SWIPER_TABS.MY) {
      residentRefetch()
    } else if (activeSection === SWIPER_TABS.CONNECTIONS) {
      connectionRefetch()
    }
  }, [currentPage, startupsRefetch, residentRefetch, connectionRefetch, activeSection, ecosystemMenu])

  const scrollToTop = (): void => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const changeTabActions = (tabValue: string): void => {
    setTabValue(tabValue)
    setSearchTerm('')
    setSearchFilter('')
    setCurrentPage(1)
    setHiddenStartupPage(1)
    setRegisterDateRange(emptyDateRange)
    setSelectedFilters({
      company_address_state: [],
      startup_info_activity: [],
      business_model: [],
      number_employees: [],
      startup_info_target: [],
      startup_info_investment_source: [],
      startup_info_development_stage: [],
      startup_info_has_raised_investment: []
    })
    scrollToTop()
    if (activeSection === SWIPER_TABS.ZING) {
      startupsRefetch()
    } else if (activeSection === SWIPER_TABS.MY) {
      residentRefetch()
    } else if (activeSection === SWIPER_TABS.CONNECTIONS) {
      connectionRefetch()
    }
  }

  const handleFilterChange = (type: string, values: ValueType[], checked: boolean): void => {
    setSelectedFilters(prev => ({
      ...prev,
      [type]: values.map(v => String(v))
    }))
  }

  const handlePublicAdministrationFilterChange = (type: string, values: ValueType[], checked: boolean): void => {
    setPublicAdministrationSelectedFilters(prev => ({
      ...prev,
      [type]: values.map(v => String(v))
    }))
  }

  const onSearch = (value: string): void => {
    setSearchTerm(value)
    setSearchFilter(value)
  }

  const onPublicAdministrationSearch = (value: string): void => {
    setPublicAdministrationSearchTerm(value)
    setPublicAdministrationSearchFilter(value)
  }

  const handlePageChange = (eventKey: string | number): void => {
    const newPage = Number(eventKey)
    setCurrentPage(newPage)
    scrollToTop()
  }

  const handlePrevPage = (): void => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
      scrollToTop()
      if (activeSection === SWIPER_TABS.ZING) {
        startupsRefetch()
      } else if (activeSection === SWIPER_TABS.MY) {
        residentRefetch()
      } else if (activeSection === SWIPER_TABS.CONNECTIONS) {
        connectionRefetch()
      }
    }
  }

  const handleNextPage = (): void => {
    const total = activeSection === SWIPER_TABS.ZING
      ? totalRows
      : activeSection === SWIPER_TABS.MY
        ? residentTotalRows
        : connectionTotalRows
    if (currentPage < Math.ceil(total / pageSize)) {
      setCurrentPage(currentPage + 1)
      scrollToTop()
      if (activeSection === SWIPER_TABS.ZING) {
        startupsRefetch()
      } else if (activeSection === SWIPER_TABS.MY) {
        residentRefetch()
      } else if (activeSection === SWIPER_TABS.CONNECTIONS) {
        connectionRefetch()
      }
    }
  }

  const postMutation = postProvider(
    async () => {
      if (ecosystemMenu === 'startups') {
        startupsRefetch()
        residentRefetch()
        connectionRefetch()
      } else {
        publicAdministrationAllRefetch()
        publicAdministrationResidentRefetch()
        if (canHideEcosystemItems) publicAdministrationHiddenRefetch()
      }
    }
  )

  const connectionMutation = postProvider(
    async () => {
      startupsRefetch()
      residentRefetch()
      connectionRefetch()
      showToast('success', 'Conexão criada com sucesso!')
    },
    async (error: any) => {
      console.error('Erro ao criar conexão:', error)
      showToast('error', 'Erro ao criar conexão. Tente novamente.')
    }
  )

  const quickConnectionMutation = postProvider(
    async (success: any) => {
      try {
        const responseData = success?.data
        const isEmailAlreadyInUse = responseData?.isEmailAlreadyInUse === true
        const existingStartupId = responseData?.startupId
        startupsRefetch()
        residentRefetch()
        connectionRefetch()
        if (isEmailAlreadyInUse) {
          showModal('startupAlreadyExists', {
            title: 'Startup já está cadastrada em nossa base!',
            message: <StartupAlreadyExistsModal />,
            onConfirm: () => {
              if (lastQuickConnectionData == null) {
                showToast('error', 'Não foi possível recuperar os dados da conexão rápida.')
                return
              }
              const sourceVal = lastQuickConnectionData.source ?? ''
              const responsibleVal = lastQuickConnectionData.responsible ?? ''
              const areaInterestVal = lastQuickConnectionData.areaInterest ?? ''
              const descriptionVal = lastQuickConnectionData.description ?? ''

              const payload = {
                url: '/v1/hub/company/ecosystem/connection',
                data: {
                  Data: {
                    startupId: existingStartupId,
                    companyId: existingStartupId,
                    source: sourceVal,
                    responsible: responsibleVal,
                    areaInterest: areaInterestVal,
                    description: descriptionVal
                  }
                }
              }
              connectionMutation.mutate(payload)
            },
            onCancel: () => {
              showToast('error', 'Erro ao criar conexão rápida. E-mail já está em uso.')
            }
          })
        } else {
          showToast('success', 'Conexão rápida criada com sucesso!')
        }
      } catch (e) {
        showToast('error', 'Erro ao processar retorno da conexão rápida.')
      }
    },
    async (error: any) => {
      console.error('Erro ao criar conexão rápida:', error)
      showToast('error', 'Erro ao criar conexão rápida. Tente novamente.')
    }
  )

  const handleConnectionConfirm = (data: any): void => {
    const payload = {
      url: '/v1/hub/company/ecosystem/connection',
      data: {
        Data: {
          startupId: data.startupId,
          companyId: data.startupId,
          source: data.source,
          responsible: data.responsible,
          areaInterest: data.areaInterest,
          description: data.description
        }
      }
    }
    connectionMutation.mutate(payload)
  }

  const handleQuickConnectionConfirm = (data: any): void => {
    setLastQuickConnectionData(data)
    const payload = {
      url: '/v1/hub/company/ecosystem/quick-connection',
      data: {
        data: {
          companyId: data.startupId,
          companyName: data.startupName,
          name: data.representant,
          email: data.startupEmail,
          contact: data.contactPhone,
          startupName: data.startupName,
          source: data.source,
          contactName: data.representant,
          responsible: data.responsible,
          areaInterest: data.areaInterest,
          description: data.description
        }
      }
    }
    quickConnectionMutation.mutate(payload)
  }
  const deleteMutation = deleteProvider(
    async () => {
      if (ecosystemMenu === 'startups') {
        startupsRefetch()
        residentRefetch()
        connectionRefetch()
      } else {
        publicAdministrationAllRefetch()
        publicAdministrationResidentRefetch()
        if (canHideEcosystemItems) publicAdministrationHiddenRefetch()
      }
    }
  )

  const ecosystemMenuItems: { key: EcosystemMenuKey; label: string }[] = [
    { key: 'startups', label: trans('Startups') },
    { key: 'publicAdministration', label: trans('Organizações públicas') }
  ]

  let publicAdministrationIsFetching: boolean
  if (publicAdministrationActiveTab === PUBLIC_ADMINISTRATION_TABS.ALL) {
    publicAdministrationIsFetching = isPublicAdministrationAllFetching
  } else if (publicAdministrationActiveTab === PUBLIC_ADMINISTRATION_TABS.RESIDENT) {
    publicAdministrationIsFetching = isPublicAdministrationResidentFetching
  } else {
    publicAdministrationIsFetching = isPublicAdministrationHiddenFetching
  }

  const handleRegisterDateRangeChange = (newRange: DateRange): void => {
    setRegisterDateRange(newRange)
    setCurrentPage(1)
    setPublicAdministrationPage(1)
  }

  const handlePublicAdministrationPageChange = (eventKey: string | number): void => {
    setPublicAdministrationPage(Number(eventKey))
    scrollToTop()
  }

  const handlePublicAdministrationPrevPage = (): void => {
    if (publicAdministrationPage > 1) {
      setPublicAdministrationPage(publicAdministrationPage - 1)
      scrollToTop()
    }
  }

  const handlePublicAdministrationNextPage = (): void => {
    if (
      publicAdministrationPage <
      Math.ceil(publicAdministrationDisplayTotalRows / publicAdministrationPageSize)
    ) {
      setPublicAdministrationPage(publicAdministrationPage + 1)
      scrollToTop()
    }
  }

  const handleHiddenPageChange = (eventKey: string | number): void => {
    setHiddenStartupPage(Number(eventKey))
    scrollToTop()
  }

  const handleHiddenPrevPage = (): void => {
    if (hiddenStartupPage > 1) {
      setHiddenStartupPage(p => p - 1)
      scrollToTop()
    }
  }

  const handleHiddenNextPage = (): void => {
    if (hiddenStartupPage < Math.ceil(hiddenStartupTotalRows / pageSize)) {
      setHiddenStartupPage(p => p + 1)
      scrollToTop()
    }
  }

  const handleExportReportDownload = (): void => {
    if (
      ecosystemMenu === 'startups' &&
      activeSection === SWIPER_TABS.CONNECTIONS
    ) {
      return
    }

    downloadExportFile(
      {
        url: '/v1/hub/company/ecosystem/report/analytical',
        httpConfig: { responseType: 'blob' }
      },
      {
        onSuccess: (res) => {
          triggerFileDownloadOnClientSide(res.data, 'relatorio-ecossistema.xlsx')
        },
        onError: () => {
          showToast('error', 'Erro ao baixar o relatório.')
        }
      }
    )
  }

  const showEcosystemExportButton =
    canManageEcosystemResidents &&
    (ecosystemMenu === 'publicAdministration' ||
      activeSection !== SWIPER_TABS.CONNECTIONS)

  const ecosystemExportButton = showEcosystemExportButton ? (
    <div className="ecosystem-export-btn">
      <BaseButton
        variant="primary-invert"
        btnContent={
          <span>
            <FontAwesomeIcon className="pr-2" icon={['fas', 'download']} />
            Exportar
          </span>
        }
        onClick={handleExportReportDownload}
      />
    </div>
  ) : null

  return (
    <Container fluid style={{ paddingLeft: '0', paddingRight: '0' }}>
      <EcosystemPageHeader>
        <EcosystemPageHeaderLeft $hasExportAction={showEcosystemExportButton}>
          <PageTitle style={{ marginBottom: 0 }}>{labels.pageTitle}</PageTitle>
          <EcosystemSubtitle>
            {trans('Explore e conecte-se com startups e organizações do ecossistema de inovação.')}
          </EcosystemSubtitle>
        </EcosystemPageHeaderLeft>
        {ecosystemExportButton != null && (
          <EcosystemPageHeaderActions>{ecosystemExportButton}</EcosystemPageHeaderActions>
        )}
      </EcosystemPageHeader>
      <div className="ecosystem-layout">
        <div className="ecosystem-sidebar">
          <SidebarWrapper>
            {ecosystemMenuItems.map(item => (
              <SidebarItem
                key={item.key}
                $isActive={ecosystemMenu === item.key}
                type="button"
                onClick={() => {
                  setEcosystemMenu(item.key)
                  setRegisterDateRange(emptyDateRange)
                }}
              >
                {item.label}
              </SidebarItem>
            ))}
          </SidebarWrapper>
        </div>
        <div className="ecosystem-content">
          {ecosystemMenu === 'publicAdministration' && (
            <>
              <div className="ecosystem-tabs-fixed">
                <Tabs
                  id="public-administration-tabs"
                  activeKey={String(publicAdministrationActiveTab)}
                  onSelect={(k) => {
                    if (k == null) return
                    setPublicAdministrationActiveTab(Number(k))
                    setPublicAdministrationPage(1)
                    setHiddenPublicAdministrationPage(1)
                    setRegisterDateRange(emptyDateRange)
                  }}
                  className="bg-transparent pt-10"
                >
                  <Tab eventKey="0" title={<TabHeader title={`Todas as organizações públicas (${publicAdministrationAllTabTotal})`} />} className="bg-transparent" />
                  <Tab eventKey="1" title={<TabHeader title={`Organizações residentes (${publicAdministrationResidentTabTotal})`} />} className="bg-transparent" />
                  {canHideEcosystemItems && (
                    <Tab eventKey="2" title={<TabHeader title={`Órgãos ocultos (${hiddenPublicAdministrationTotalRows})`} />} className="bg-transparent" />
                  )}
                </Tabs>
              </div>
              <PublicAdministrationFilterSection
                filterBarRef={filterBarRefPublicAdministration}
                searchTerm={publicAdministrationSearchTerm}
                onSearchSubmit={onPublicAdministrationSearch}
                onSearchInputChange={(val) => {
                  setPublicAdministrationSearchTerm(val)
                  if (val === '') {
                    setPublicAdministrationSearchFilter('')
                  }
                }}
                onFilterChange={handlePublicAdministrationFilterChange}
                filterCounts={publicAdministrationFilterCounts}
                totalFilterCount={publicAdministrationTotalFilterCount}
                selectedFilters={publicAdministrationSelectedFilters}
                registerDateRange={registerDateRange}
                onRegisterDateRangeChange={handleRegisterDateRangeChange}
              />
              {publicAdministrationIsFetching
                ? <Loading />
                : (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
                      {publicAdministrationDisplayCardRows.length === 0
                        ? <div>Nenhum órgão público encontrado.</div>
                        : publicAdministrationDisplayCardRows.map((publicAdministration) => (
                          <TeiaPublicAdministrationCard
                            key={publicAdministration.id}
                            title={publicAdministration.title}
                            logo={publicAdministration.logo}
                            activity={publicAdministration.activity}
                            pitch={publicAdministration.pitch}
                            dirigenteMaximo={publicAdministration.dirigenteMaximo}
                            numeroServidores={publicAdministration.numeroServidores}
                            areaInovacao={publicAdministration.innovationAreaPresence}
                            state={publicAdministration.state}
                            isInMyEcosystem={publicAdministration.isInMyEcosystem}
                            badgeLabel={
                              publicAdministration.ecosystemHidden
                                ? 'Órgão ocultado'
                                : publicAdministration.badgeLabel
                            }
                            isHidden={publicAdministration.ecosystemHidden}
                            {...(canHideEcosystemItems
                              ? {
                                onToggleHidden: () => {
                                  void togglePublicAdministrationHiddenInEcosystemList(
                                    publicAdministration
                                  )
                                }
                              }
                              : {})}
                            onViewProfile={() =>
                              navigate(`/hub/company/ecosystem/public-administration/${publicAdministration.id}`, {
                                state: {
                                  publicAdministration: mapRowToPublicAdministrationDetailState(publicAdministration),
                                  fromTab: 'publicAdministration' as const,
                                  fromSubTab: publicAdministrationActiveTab,
                                  registerDateRange
                                }
                              })}
                            {...(canManageEcosystemResidents
                              ? {
                                onAddResident: () => {
                                  showModal('confirmation', {
                                    title: 'Adicionar como residente',
                                    message: <PublicAdministrationConfirmationModal />,
                                    onConfirm: () => {
                                      postMutation.mutate({
                                        url: `/v1/hub/company/ecosystem/resident/${publicAdministration.residentCompanyId}`
                                      })
                                      showToast('success', 'Órgão público adicionado como residente!')
                                    }
                                  })
                                },
                                onRemoveResident: () => {
                                  showModal('confirmation', {
                                    title: 'Remover como residente',
                                    message: <PublicAdministrationConfirmationModal variant="remove" />,
                                    confirmButtonText: 'Remover',
                                    onConfirm: () => {
                                      if (publicAdministration.residentId !== null) {
                                        deleteMutation.mutate({
                                          url: `/v1/hub/company/ecosystem/resident/${publicAdministration.residentId}`
                                        })
                                        showToast('success', 'Órgão público removido como residente!')
                                      }
                                    }
                                  })
                                }
                              }
                              : {})}
                          />
                        ))}
                    </div>
                    <div className="pagination-footer">
                      <div className="d-flex align-items-center">
                        <button
                          className="btn btn-link p-0 me-2"
                          onClick={handlePublicAdministrationPrevPage}
                          disabled={publicAdministrationPage === 1}
                          style={{
                            border: 'none',
                            background: 'none',
                            cursor: publicAdministrationPage === 1 ? 'not-allowed' : 'pointer',
                            opacity: publicAdministrationPage === 1 ? 0.5 : 1,
                            color: '#0066B3',
                            visibility: 'visible'
                          }}
                          type="button"
                        >
                          <i className="bi bi-chevron-left" style={{ fontSize: '1.2rem' }}></i>
                        </button>
                        <Pagination
                          activePage={publicAdministrationPage}
                          pages={Math.max(
                            1,
                            Math.ceil(publicAdministrationDisplayTotalRows / publicAdministrationPageSize)
                          )}
                          maxButtons={3}
                          ellipsis={true}
                          boundaryLinks={true}
                          onSelect={handlePublicAdministrationPageChange}
                          size="sm"
                          total={publicAdministrationDisplayTotalRows}
                          className="custom-pagination"
                        />
                        <button
                          className="btn btn-link p-0 ms-2"
                          onClick={handlePublicAdministrationNextPage}
                          disabled={
                            publicAdministrationPage >=
                              Math.ceil(
                                publicAdministrationDisplayTotalRows / publicAdministrationPageSize
                              ) || publicAdministrationDisplayTotalRows === 0
                          }
                          style={{
                            border: 'none',
                            background: 'none',
                            cursor:
                              publicAdministrationPage >=
                                Math.ceil(
                                  publicAdministrationDisplayTotalRows /
                                    publicAdministrationPageSize
                                ) || publicAdministrationDisplayTotalRows === 0
                                ? 'not-allowed'
                                : 'pointer',
                            opacity:
                              publicAdministrationPage >=
                                Math.ceil(
                                  publicAdministrationDisplayTotalRows /
                                    publicAdministrationPageSize
                                ) || publicAdministrationDisplayTotalRows === 0
                                ? 0.5
                                : 1,
                            color: '#0066B3',
                            visibility: 'visible'
                          }}
                          type="button"
                        >
                          <i className="bi bi-chevron-right" style={{ fontSize: '1.2rem' }}></i>
                        </button>
                      </div>
                      <div className="ms-3 items-per-page" style={{ color: '#0066B3' }}>
                        {publicAdministrationPageSize} itens por página
                      </div>
                    </div>
                  </>
                )}
            </>
          )}
          {ecosystemMenu === 'startups' && (
            <>
              <div className="ecosystem-tabs-fixed">
                <Tabs
                  id="ecosystem-tabs"
                  activeKey={String(activeSection)}
                  onSelect={(k) => k != null && setActiveSection(Number(k))}
                  className="bg-transparent pt-10"
                >
                  <Tab eventKey="0" title={<TabHeader title={`Todas as startups (${totalRows})`} />} className="bg-transparent" />
                  <Tab eventKey="1" title={<TabHeader title={`Startups residentes (${residentTotalRows})`} />} className="bg-transparent" />
                  <Tab eventKey="2" title={<TabHeader title={`Conexões (${connectionTotalRows})`} />} className="bg-transparent" />
                  {canHideEcosystemItems && (
                    <Tab eventKey="3" title={<TabHeader title={`Startups ocultas (${hiddenStartupTotalRows})`} />} className="bg-transparent" />
                  )}
                </Tabs>
              </div>
              <div ref={filterBarRefStartups} className='d-flex align-items-center ecosystem-filter-bar' style={{ position: 'relative', width: '100%', gap: '8px', marginTop: '30px', marginBottom: '30px' }}>
                <div className="ecosystem-filter-bar-search" style={{ flex: 1, minWidth: 0 }}>
                  <Filter
                    filterBarRef={filterBarRefStartups}
                    currentSearchTerm={searchTerm}
                    btnClick={onSearch}
                    onFilterChange={(val) => {
                      setSearchTerm(val)
                      if (val === '') {
                        setSearchFilter(val)
                      }
                    }}
                    minSearchTermLength={1}
                    filterButtonNode={
                      activeSection !== SWIPER_TABS.CONNECTIONS
                        ? <FilterButton filterBarRef={filterBarRefStartups} startupData={getCurrentFilters()} onFilterChange={handleFilterChange} filterCounts={filterCounts} totalFilterCount={totalFilterCount} selectedFilters={selectedFilters} />
                        : undefined
                    }
                  />
                </div>
                {activeSection !== SWIPER_TABS.CONNECTIONS && (
                  <div className="ecosystem-filtrar-wrapper">
                    <FilterButton filterBarRef={filterBarRefStartups} startupData={getCurrentFilters()} onFilterChange={handleFilterChange} filterCounts={filterCounts} totalFilterCount={totalFilterCount} selectedFilters={selectedFilters} />
                  </div>
                )}
                <div className="ecosystem-filter-bar-actions d-flex align-items-center" style={{ gap: '8px' }}>
                  {activeSection === SWIPER_TABS.CONNECTIONS
                    ? (
                      <div className="ecosystem-filter-desktop-only" style={{ flexShrink: 0, width: '167px', minWidth: '167px', justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          style={{
                            display: 'flex',
                            height: '32px',
                            width: '167px',
                            padding: '0 16px',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '8px',
                            background: '#0066B3',
                            color: '#FFFFFF',
                            fontSize: '14px',
                            fontStyle: 'normal',
                            fontWeight: 700,
                            lineHeight: '140%',
                            cursor: 'pointer',
                            border: 'none',
                            borderRadius: '4px'
                          }}
                          onClick={() => showModal('quickConnect', {
                            title: 'Conexão rápida',
                            message: <QuickConnectModal startupId={crypto.randomUUID()} />,
                            onConfirm: handleQuickConnectionConfirm
                          })}
                        >
                          <span style={{ fontSize: '16px', lineHeight: 1 }}>+</span>
                          <span>Conexão rápida</span>
                        </button>
                      </div>
                    )
                    : (
                      <div className="ecosystem-date-filter" style={{ flexShrink: 0 }}>
                        <DateRegisterFilter
                          value={registerDateRange}
                          onChange={handleRegisterDateRangeChange}
                          isArrow={false}
                        />
                      </div>
                    )}
                </div>
              </div>
              {isStartupsFetching && activeSection === SWIPER_TABS.ZING
                ? <Loading />
                : isResidentFetching && activeSection === SWIPER_TABS.MY
                  ? <Loading />
                  : <>
                    {activeSection === SWIPER_TABS.ZING && (
                      <>
                        {(getFilteredDataset().length === 0)
                          ? (
                            <div>Nenhuma startup encontrada.</div>
                          )
                          : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                              {getFilteredDataset().map((startup) => {
                                return <TeiaNewStartupCard
                                  key={startup.id}
                                  title={startup.companyName}
                                  pitch={startup.startupInfo.pitch}
                                  logo={typeof startup.profileImage === 'string' ? startup.profileImage : undefined}
                                  activity={startup.startupInfo.activity}
                                  numberEmployees={startup.startupInfo.numberEmployees}
                                  businessModel={startup.startupInfo.businessModel}
                                  target={startup.startupInfo.target}
                                  state={startup.address.state}
                                  isInMyEcosystem={startup.resident_id !== null}
                                  badgeLabel={
                                    isStartupHidden(startup)
                                      ? 'Startup ocultada'
                                      : startup.resident_id !== null
                                        ? 'Startup residente'
                                        : ''
                                  }
                                  isHidden={isStartupHidden(startup)}
                                  onViewProfile={() => navigate(`/hub/company/ecosystem/${startup.id}`, { state: { startup, fromTab: 'startups' as const, fromSubTab: activeSection, registerDateRange } })}
                                  {...(canHideEcosystemItems
                                    ? {
                                      onToggleHidden: () => {
                                        void toggleStartupHiddenInEcosystemList(startup)
                                      }
                                    }
                                    : {})}
                                  {...(canManageEcosystemResidents
                                    ? {
                                      onAddResident: () => {
                                        showModal('confirmation', {
                                          title: 'Adicionar residente',
                                          message: <ConfirmationModal isAddResident={true} />,
                                          onConfirm: () => {
                                            postMutation.mutate({
                                              url: `/v1/hub/company/ecosystem/resident/${ecosystemStartupResidentCompanyId(startup)}`
                                            })
                                            showToast('success', labels.addResidentSuccess)
                                          }
                                        })
                                      },
                                      onRemoveResident: () => {
                                        showModal('confirmation', {
                                          title: 'Remover residente',
                                          message: <ConfirmationModal isAddResident={false} />,
                                          onConfirm: () => {
                                            if (startup.resident_id !== null) {
                                              deleteMutation.mutate({ url: `/v1/hub/company/ecosystem/resident/${startup.resident_id}` })
                                              showToast('success', labels.removeResidentSuccess)
                                            }
                                          }
                                        })
                                      }
                                    }
                                    : {})}
                                  onConnect={() => {
                                    showModal('connect', {
                                      startupId: startup.id,
                                      title: 'Conexão',
                                      message: <ConnectModal startupId={startup.id} />,
                                      onConfirm: handleConnectionConfirm
                                    })
                                  }}
                                />
                              })}
                            </div>
                          )}
                        <div className="pagination-footer">
                          <div className="d-flex align-items-center">
                            <button
                              className="btn btn-link p-0 me-2"
                              onClick={handlePrevPage}
                              disabled={currentPage === 1}
                              style={{
                                border: 'none',
                                background: 'none',
                                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                opacity: currentPage === 1 ? 0.5 : 1,
                                color: '#0066B3',
                                visibility: 'visible'
                              }}
                            >
                              <i className="bi bi-chevron-left" style={{ fontSize: '1.2rem' }}></i>
                            </button>
                            <Pagination
                              activePage={currentPage}
                              pages={Math.ceil(totalRows / pageSize)}
                              maxButtons={3}
                              ellipsis={true}
                              boundaryLinks={true}
                              onSelect={handlePageChange}
                              size="sm"
                              total={totalRows}
                              className="custom-pagination"
                            />
                            <button
                              className="btn btn-link p-0 ms-2"
                              onClick={handleNextPage}
                              disabled={currentPage === Math.ceil(totalRows / pageSize)}
                              style={{
                                border: 'none',
                                background: 'none',
                                cursor: currentPage === Math.ceil(totalRows / pageSize) ? 'not-allowed' : 'pointer',
                                opacity: currentPage === Math.ceil(totalRows / pageSize) ? 0.5 : 1,
                                color: '#0066B3',
                                visibility: 'visible'
                              }}
                            >
                              <i className="bi bi-chevron-right" style={{ fontSize: '1.2rem' }}></i>
                            </button>
                          </div>
                          <div className="ms-3 items-per-page" style={{ color: '#0066B3' }}>
                            {pageSize} itens por página
                          </div>
                        </div>
                      </>
                    )}
                    {activeSection === SWIPER_TABS.MY && (
                      <>
                        {(filteredResidents.length === 0)
                          ? (
                            <div>Nenhuma startup encontrada.</div>
                          )
                          : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                              {filteredResidents.map((startup) => {
                                return <TeiaNewStartupCard
                                  key={startup.id}
                                  title={startup.companyName}
                                  pitch={startup.startupInfo.pitch}
                                  logo={typeof startup.profileImage === 'string' ? startup.profileImage : undefined}
                                  activity={startup.startupInfo.activity}
                                  numberEmployees={startup.startupInfo.numberEmployees}
                                  businessModel={startup.startupInfo.businessModel}
                                  target={startup.startupInfo.target}
                                  state={startup.address.state}
                                  isInMyEcosystem={true}
                                  badgeLabel={
                                    isStartupHidden(startup) ? 'Startup ocultada' : 'Startup residente'
                                  }
                                  isHidden={isStartupHidden(startup)}
                                  onViewProfile={() => navigate(`/hub/company/ecosystem/${startup.id}`, { state: { startup, fromTab: 'startups' as const, fromSubTab: activeSection, registerDateRange } })}
                                  {...(canHideEcosystemItems
                                    ? {
                                      onToggleHidden: () => {
                                        void toggleStartupHiddenInEcosystemList(startup)
                                      }
                                    }
                                    : {})}
                                  {...(canManageEcosystemResidents
                                    ? {
                                      onAddResident: () => {
                                        showModal('confirmation', {
                                          title: 'Adicionar residente',
                                          message: <ConfirmationModal isAddResident={true} />
                                        })
                                      },
                                      onRemoveResident: () => {
                                        showModal('confirmation', {
                                          title: 'Remover residente',
                                          message: <ConfirmationModal isAddResident={false} />,
                                          onConfirm: () => {
                                            if (startup.resident_id !== null) {
                                              deleteMutation.mutate({ url: `/v1/hub/company/ecosystem/resident/${startup.resident_id}` })
                                              showToast('success', labels.removeResidentSuccess)
                                            }
                                          }
                                        })
                                      }
                                    }
                                    : {})}
                                  onConnect={() => {
                                    showModal('connect', {
                                      startupId: startup.id,
                                      title: 'Conexão',
                                      message: <ConnectModal startupId={startup.id} />,
                                      onConfirm: handleConnectionConfirm
                                    })
                                  }}
                                />
                              })}
                            </div>
                          )}
                        <div className="pagination-footer">
                          <div className="d-flex align-items-center">
                            <button
                              className="btn btn-link p-0 me-2"
                              onClick={handlePrevPage}
                              disabled={currentPage === 1}
                              style={{
                                border: 'none',
                                background: 'none',
                                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                opacity: currentPage === 1 ? 0.5 : 1,
                                color: '#0066B3',
                                visibility: 'visible'
                              }}
                            >
                              <i className="bi bi-chevron-left" style={{ fontSize: '1.2rem' }}></i>
                            </button>
                            <Pagination
                              activePage={currentPage}
                              pages={Math.ceil(residentTotalRows / pageSize)}
                              maxButtons={3}
                              ellipsis={true}
                              boundaryLinks={true}
                              onSelect={handlePageChange}
                              size="sm"
                              total={residentTotalRows}
                              className="custom-pagination"
                            />
                            <button
                              className="btn btn-link p-0 ms-2"
                              onClick={handleNextPage}
                              disabled={currentPage === Math.ceil(residentTotalRows / pageSize)}
                              style={{
                                border: 'none',
                                background: 'none',
                                cursor: currentPage === Math.ceil(residentTotalRows / pageSize) ? 'not-allowed' : 'pointer',
                                opacity: currentPage === Math.ceil(residentTotalRows / pageSize) ? 0.5 : 1,
                                color: '#0066B3',
                                visibility: 'visible'
                              }}
                            >
                              <i className="bi bi-chevron-right" style={{ fontSize: '1.2rem' }}></i>
                            </button>
                          </div>
                          <div className="ms-3 items-per-page" style={{ color: '#0066B3' }}>
                            {pageSize} itens por página
                          </div>
                        </div>
                      </>
                    )}
                    {activeSection === SWIPER_TABS.CONNECTIONS && (
                      (() => {
                        const filteredItems = mapConnectionsToTableData().filter((item) => {
                          const term = searchFilter.trim().toLowerCase()
                          if (term === '') return true
                          return String(item.startup).toLowerCase().includes(term)
                        })

                        if (filteredItems.length === 0) {
                          return (
                            <div style={{ display: 'flex', width: '100%', justifyContent: 'center', marginTop: '32px' }}>
                              <div style={{ display: 'inline-flex', padding: '10px', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                <img src={backgroundNoConnection} alt="Sem conexões" width={320} height={282} />
                              </div>
                            </div>
                          )
                        }

                        return (
                          <>
                            <div className="connections-table-wrapper">
                              <HubStartupCompleteConnectionsTable items={filteredItems} />
                            </div>
                            <div className="pagination-footer">
                              <div className="d-flex align-items-center">
                                <button
                                  className="btn btn-link p-0 me-2"
                                  onClick={handlePrevPage}
                                  disabled={currentPage === 1}
                                  style={{
                                    border: 'none',
                                    background: 'none',
                                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                    opacity: currentPage === 1 ? 0.5 : 1,
                                    color: '#0066B3',
                                    visibility: 'visible'
                                  }}
                                >
                                  <i className="bi bi-chevron-left" style={{ fontSize: '1.2rem' }}></i>
                                </button>
                                <Pagination
                                  activePage={currentPage}
                                  pages={Math.ceil(connectionTotalRows / pageSize)}
                                  maxButtons={3}
                                  ellipsis={true}
                                  boundaryLinks={true}
                                  onSelect={handlePageChange}
                                  size="sm"
                                  total={connectionTotalRows}
                                  className="custom-pagination"
                                />
                                <button
                                  className="btn btn-link p-0 ms-2"
                                  onClick={handleNextPage}
                                  disabled={currentPage === Math.ceil(connectionTotalRows / pageSize)}
                                  style={{
                                    border: 'none',
                                    background: 'none',
                                    cursor: currentPage === Math.ceil(connectionTotalRows / pageSize) ? 'not-allowed' : 'pointer',
                                    opacity: currentPage === Math.ceil(connectionTotalRows / pageSize) ? 0.5 : 1,
                                    color: '#0066B3',
                                    visibility: 'visible'
                                  }}
                                >
                                  <i className="bi bi-chevron-right" style={{ fontSize: '1.2rem' }}></i>
                                </button>
                              </div>
                              <div className="ms-3 items-per-page" style={{ color: '#0066B3' }}>
                                {pageSize} itens por página
                              </div>
                            </div>
                          </>
                        )
                      })()
                    )}
                    {activeSection === SWIPER_TABS.HIDDEN && canHideEcosystemItems && (
                      <>
                        {isHiddenStartupFetching
                          ? <Loading />
                          : hiddenStartups.length === 0
                            ? <div>Nenhuma startup oculta encontrada.</div>
                            : (
                              <div className="hidden-startups-list">
                                {hiddenStartups.map((startup) => (
                                  <TeiaNewStartupCard
                                    key={startup.id}
                                    title={startup.companyName}
                                    pitch={startup.startupInfo.pitch}
                                    logo={typeof startup.profileImage === 'string' ? startup.profileImage : undefined}
                                    activity={startup.startupInfo.activity}
                                    numberEmployees={startup.startupInfo.numberEmployees}
                                    businessModel={startup.startupInfo.businessModel}
                                    target={startup.startupInfo.target}
                                    state={startup.address.state}
                                    isInMyEcosystem={startup.resident_id !== null}
                                    badgeLabel="Startup ocultada"
                                    isHidden={true}
                                    onViewProfile={() => navigate(`/hub/company/ecosystem/${startup.id}`, {
                                      state: { startup, fromTab: 'startups' as const, fromSubTab: activeSection, registerDateRange }
                                    })}
                                    onToggleHidden={() => { void toggleStartupHiddenInEcosystemList(startup) }}
                                  />
                                ))}
                              </div>
                            )}
                        <div className="pagination-footer">
                          <div className="d-flex align-items-center">
                            <button
                              className="btn btn-link p-0 me-2 pagination-nav-btn"
                              onClick={handleHiddenPrevPage}
                              disabled={hiddenStartupPage === 1}
                            >
                              <i className="bi bi-chevron-left chevron-icon"></i>
                            </button>
                            <Pagination
                              activePage={hiddenStartupPage}
                              pages={Math.max(1, Math.ceil(hiddenStartupTotalRows / pageSize))}
                              maxButtons={3}
                              ellipsis={true}
                              boundaryLinks={true}
                              onSelect={handleHiddenPageChange}
                              size="sm"
                              total={hiddenStartupTotalRows}
                              className="custom-pagination"
                            />
                            <button
                              className="btn btn-link p-0 ms-2 pagination-nav-btn"
                              onClick={handleHiddenNextPage}
                              disabled={hiddenStartupPage >= Math.ceil(hiddenStartupTotalRows / pageSize) || hiddenStartupTotalRows === 0}
                            >
                              <i className="bi bi-chevron-right chevron-icon"></i>
                            </button>
                          </div>
                          <div className="ms-3 items-per-page">
                            {pageSize} itens por página
                          </div>
                        </div>
                      </>
                    )}
                  </>
              }
            </>
          )}
        </div>
      </div>
    </Container>
  )
}
